import { Menu } from '@mui/material';
import { saveAs } from 'file-saver';
import { sortBy } from 'lodash-es';
import bind from 'memoize-bind';
import moment from 'moment-timezone';
import React, { Component, Fragment } from 'react';

import { Checkbox } from '@sbu/components.checkbox';
import { Link } from '@sbu/components.link';
import { ToastsContext } from '@sbu/contexts.toasts';
import {
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from '@sumologic/yama-ds-components';
import { AngleDownIcon, ExportIcon, LinkIcon } from '@sumologic/yama-ds-icons';

import { JsonTree } from 'shared/components/data/Tree';
import { ContextActionContainer } from 'shared/components/internal/ContextAction';
import {
  getRecordCategory,
  getRecordSchemaVersion,
  RecordOverview,
  RecordType,
} from 'shared/components/internal/Records';
import { SumoRawMessageContainer } from 'shared/components/internal/SumoRawMessage';
import { Collapse } from 'shared/components/layout/Collapse';

import { type EntityTypeFieldsAndIndicator } from '../../useEntityTypeRecordFields';
import { NetworkRecord, parseRecord } from '../schema/legacy';
import css from './RecordDetails.module.scss';
import RecordDetailsViewTabs from './RecordDetailsViewTabs';
import { ViewType } from './types';

interface RecordDetailsProps {
  entityTypeFieldsAndIndicator?: EntityTypeFieldsAndIndicator;
  record: RecordType;
  normalizedSearchUrl: string;
  rawSearchUrl: string;
}

interface RecordDetailsState {
  menuAnchorEl: HTMLElement | null;
  rawMessage?: string;
  showEmpty: boolean;
  view: ViewType;
}

export class RecordDetails extends Component<
  RecordDetailsProps,
  RecordDetailsState
> {
  context: any;
  static contextType = ToastsContext;

  state: RecordDetailsState = {
    showEmpty: false,
    view: 'overview',
    menuAnchorEl: null,
  };

  changeView(view: ViewType) {
    this.setState({ view });
  }

  toggleEmptyToggle({ target }: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ showEmpty: target.checked });
  }

  getRecordDetails() {
    // Sort records by display labels.
    return sortBy(
      parseRecord(this.props.record, this.state.showEmpty),
      (prop) => prop.label
    );
  }

  onMenuOpen(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    this.setState({ menuAnchorEl: e.currentTarget });
  }

  onMenuClose() {
    this.setState({ menuAnchorEl: null });
  }

  renderDivider() {
    return (
      <Divider
        orientation='vertical'
        sx={{ left: 0, position: 'absolute', top: 0 }}
      />
    );
  }

  onDownloadClicked(event: React.MouseEvent) {
    event.preventDefault();

    const { record } = this.props;
    const { uid } = record;

    try {
      const file = new File(
        [JSON.stringify(record, null, 2)],
        `record-${uid}.json`,
        {
          type: 'text/plain;charset=utf-8',
        }
      );

      saveAs(file);
    } catch {
      this.context.addError('Failed to download record');
    }
  }

  renderEmptyToggle() {
    return (
      <div className={css.emptyToggle}>
        <Checkbox
          CheckboxProps={{
            checked: this.state.showEmpty,
            onChange: bind(this.toggleEmptyToggle, this),
          }}
          label='Show Empty'
          TypographyProps={{
            whiteSpace: 'nowrap',
          }}
        />
      </div>
    );
  }

  renderLogSearch(showEmptyToggle: boolean) {
    const { metadata_sourceMessageId, timestamp } = this.props.record ?? {};
    if (!metadata_sourceMessageId || !timestamp) {
      return null;
    }
    const { menuAnchorEl } = this.state;
    const menuVisible = Boolean(menuAnchorEl);

    const { normalizedSearchUrl, rawSearchUrl } = this.props;

    const viewLinkData = [
      {
        'data-metric-id': 'view-in-log-search-normalized-link',
        label: 'Normalized Data',
        url: normalizedSearchUrl,
      },
      {
        'data-metric-id': 'view-in-log-search-raw-link',
        label: 'Raw Data',
        url: rawSearchUrl,
      },
    ];

    return (
      <div className={css.downloadButtonContainer}>
        {showEmptyToggle && this.renderDivider()}
        <Button
          className={css.timelineArrow}
          endIcon={<AngleDownIcon />}
          onClick={bind(this.onMenuOpen, this)}
          color='secondary'
        >
          View in Log Search
        </Button>
        <Menu
          anchorEl={menuAnchorEl}
          onClose={bind(this.onMenuClose, this)}
          open={menuVisible}
          sx={{
            '& a.MuiLink-root:link, & a.MuiLink-root:visited': {
              color: 'text.primary',
              textDecoration: 'none',
            },
          }}
        >
          {viewLinkData.map(
            ({ 'data-metric-id': dataMetricId, label, url }) => (
              <MenuItem
                key={dataMetricId}
                component={Link}
                data-metric-id={dataMetricId}
                href={url}
                rel='noopener'
                target='_blank'
                variant='a'
              >
                <ListItemIcon>
                  <LinkIcon />
                </ListItemIcon>
                <ListItemText primary={label} />
              </MenuItem>
            )
          )}
        </Menu>
      </div>
    );
  }

  renderDownloadButton(showEmptyToggle: boolean) {
    return (
      <div className={css.downloadButtonContainer}>
        {showEmptyToggle && this.renderDivider()}
        <Button
          className={css.downloadButton}
          onClick={bind(this.onDownloadClicked, this)}
          color='secondary'
          startIcon={<ExportIcon />}
          sx={{ ml: 1 }}
        >
          Download
        </Button>
      </div>
    );
  }

  renderLegacyOverview() {
    const { record } = this.props;
    const category = getRecordCategory(record.type);

    return (
      <Fragment>
        {category === 'network' && (
          <NetworkRecord record={record} showEmpty={this.state.showEmpty} />
        )}
        {category !== 'network' && this.renderGridView()}
      </Fragment>
    );
  }

  renderGridView() {
    const {
      record: { timestamp },
    } = this.props;

    return (
      <div className={css.recordDetails}>
        {this.getRecordDetails().map((prop) => (
          <div className={css.recordDetail} key={prop.name}>
            <span className={css.metadataLabel}>{prop.label}:</span>
            <span
              className={css.metadataValue}
              data-sumo-test='record-metadata-value'
            >
              <ContextActionContainer
                contextData={this.props.record}
                eventTimestamp={
                  timestamp ? moment.unix(timestamp / 1000).toDate() : undefined
                }
              >
                {prop.value === undefined ? (
                  <span className={css.emptyResult}>None</span>
                ) : (
                  prop.value
                )}
              </ContextActionContainer>
            </span>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { record, entityTypeFieldsAndIndicator } = this.props;
    const { showEmpty, view } = this.state;
    const schemaVersion = getRecordSchemaVersion(record);
    const showEmptyToggle = schemaVersion !== 'v3';

    // TODO: There is a bug in Slant that requires the string '0px' to work.
    return (
      <div className={css.record}>
        <div className={css.tabRegion}>
          <RecordDetailsViewTabs
            record={this.props.record}
            view={view}
            onChangeView={bind(this.changeView, this)}
          />
          {showEmptyToggle && this.renderEmptyToggle()}
          {this.renderLogSearch(showEmptyToggle)}
          {this.renderDownloadButton(showEmptyToggle)}
        </div>
        <div className={css.dataRegion}>
          <div className={css.divider}>
            <Divider />
          </div>
          <Collapse expanded={view === 'overview'}>
            {schemaVersion === 'legacy' && this.renderLegacyOverview()}
            {schemaVersion === 'v3' && (
              <RecordOverview
                record={record}
                entityTypeFieldsAndIndicator={entityTypeFieldsAndIndicator}
                showEmpty={showEmpty}
              />
            )}
          </Collapse>
          <Collapse expanded={view === 'fullDetails'}>
            <JsonTree data={record} showEmpty={showEmpty} />
          </Collapse>
          <Collapse expanded={view === 'raw'}>
            <SumoRawMessageContainer record={record} />
          </Collapse>
        </div>
      </div>
    );
  }
}
